import { ColumnDef } from '@tanstack/react-table';
import { t } from 'i18next';
import { EllipsisVerticalIcon, PencilIcon, RefreshCcwIcon, Trash2Icon } from 'lucide-react';

import { RowActionIndicator } from '@/components/atoms/RowActionIndicator/RowActionIndicator';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Switch } from '@/components/ui/switch';
import { SortableHeader } from '@/components/ui/table/sortable-header';
import type { Member } from '@/shared/types/member';
import { timeFromNow } from '@/util/dateUtils';

import { type McpServer } from './hooks/useMcpServers';

interface ColumnsProps {
  onToggleActive: (server: McpServer, isActive: boolean) => void;
  onDelete: (server: McpServer) => void;
  onResync: (server: McpServer) => void;
  onEdit: (server: McpServer) => void;
  getMember: (userId: string) => Member | undefined;
  canWrite?: boolean;
}

export const createColumns = ({
  onToggleActive,
  onDelete,
  onResync,
  onEdit,
  getMember,
  canWrite = true,
}: ColumnsProps): ColumnDef<McpServer>[] => {
  return [
    {
      id: 'spacer',
      header: '',
      enableSorting: false,
      cell: () => null,
      size: 16,
      minSize: 16,
      maxSize: 16,
      meta: { className: 'pl-2' },
    },
    {
      accessorKey: 'name',
      header: ({ column }) => <SortableHeader column={column}>{t('common.name')}</SortableHeader>,
      cell: ({ row }) => {
        const server = row.original;
        const toolsCount = server.tools?.length ?? 0;
        return (
          <div className="flex min-w-0 items-center gap-3">
            <div className="flex min-w-0 flex-col gap-1">
              <span className="truncate font-medium">{server.name}</span>
              <span className="truncate text-sm text-muted-foreground">
                {t('mcp.tools_enabled', { 0: toolsCount })}
              </span>
            </div>
            <RowActionIndicator action="open" />
          </div>
        );
      },
      enableColumnFilter: true,
      minSize: 160,
      maxSize: 320,
    },
    {
      accessorKey: 'url',
      header: t('mcp.url'),
      enableSorting: false,
      size: 240,
      minSize: 180,
      maxSize: 320,
      cell: ({ row }) => {
        return (
          <div className="min-w-0">
            <span className="block truncate text-foreground">{row.original.url}</span>
          </div>
        );
      },
    },
    {
      id: 'created_by',
      header: t('common.created_by'),
      enableSorting: false,
      size: 96,
      minSize: 80,
      maxSize: 120,
      cell: ({ row }) => {
        const createdBy = row.original.created_by;
        if (!createdBy) {
          return <span className="text-muted-foreground">-</span>;
        }
        const member = getMember(createdBy);
        const fallback = member?.name?.charAt(0) || 'S';
        return (
          <Avatar size="xs">
            <AvatarImage src={member?.avatar || ''} alt={member?.name || ''} />
            <AvatarFallback>{fallback}</AvatarFallback>
          </Avatar>
        );
      },
    },
    {
      accessorKey: 'last_used_at',
      header: ({ column }) => <SortableHeader column={column}>{t('mcp.last_used')}</SortableHeader>,
      cell: ({ row }) => {
        const lastUsed = row.original.last_used_at;
        return lastUsed ? (
          <span className="truncate text-foreground">{timeFromNow(lastUsed)}</span>
        ) : (
          <span className="text-muted-foreground">-</span>
        );
      },
      size: 120,
      minSize: 100,
      maxSize: 160,
    },
    {
      accessorKey: 'tools_status',
      header: ({ column }) => <SortableHeader column={column}>{t('mcp.status')}</SortableHeader>,
      cell: ({ row }) => {
        const status = row.original.tools_status;
        if (status === 'ok') {
          return (
            <Badge type="secondary" flavour="successful" rounded={false}>
              {t('mcp.tools_status_ok')}
            </Badge>
          );
        }
        if (status === 'needs_sync') {
          return (
            <Badge type="secondary" flavour="warning" rounded={false}>
              {t('mcp.tools_status_needs_sync')}
            </Badge>
          );
        }
        if (status === 'unreachable') {
          return (
            <Badge type="secondary" flavour="destructive" rounded={false}>
              {t('mcp.tools_status_unreachable')}
            </Badge>
          );
        }
        return null;
      },
      size: 120,
      minSize: 100,
      maxSize: 140,
    },
    {
      id: 'enable',
      header: t('mcp.enable'),
      enableSorting: false,
      size: 80,
      minSize: 72,
      maxSize: 96,
      cell: ({ row }) => {
        const server = row.original;
        return (
          <Switch
            checked={server.is_active}
            onCheckedChange={(checked) => onToggleActive(server, checked)}
            onClick={(e) => e.stopPropagation()}
            disabled={!canWrite}
          />
        );
      },
    },
    {
      id: 'actions',
      header: '',
      enableSorting: false,
      size: 56,
      minSize: 48,
      maxSize: 64,
      meta: { className: 'pr-2' },
      cell: ({ row }) => {
        const server = row.original;
        return (
          <DropdownMenu modal={false}>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost-neutral" size="icon-sm" onClick={(e) => e.stopPropagation()}>
                <EllipsisVerticalIcon size={16} />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              onClick={(e) => e.stopPropagation()}
              onPointerDown={(e) => e.stopPropagation()}
            >
              <DropdownMenuItem
                onSelect={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onEdit(server);
                }}
                className="flex items-center gap-2"
                disabled={!canWrite}
              >
                <PencilIcon size={16} className="text-muted-foreground" />
                {t('common.edit')}…
              </DropdownMenuItem>
              {server.tools_status === 'needs_sync' && (
                <DropdownMenuItem
                  onSelect={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onResync(server);
                  }}
                  className="flex items-center gap-2"
                  disabled={!canWrite}
                >
                  <RefreshCcwIcon size={16} className="text-muted-foreground" />
                  {t('mcp.resync')}
                </DropdownMenuItem>
              )}
              <DropdownMenuItem
                onSelect={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onDelete(server);
                }}
                variant="destructive"
                className="flex items-center gap-2"
                disabled={!canWrite}
              >
                <Trash2Icon size={16} />
                {t('common.delete')}…
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    },
  ];
};
